import React, { useState, useEffect } from 'react';
import { Search, MapPin, ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useGoogleMaps } from '../hooks/useGoogleMaps';

const MalaysiaClinicFinder = () => {
  const { isLoaded: googleMapsLoaded, loadError } = useGoogleMaps();
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [mapError, setMapError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [clinicsPerPage] = useState(5);
  const [searchParams, setSearchParams] = useState({
    state: 'Kuala Lumpur',
    clinicName: ''
  });

  // Replace your existing getPlaceDetails function with this enhanced version:

  const getPlaceDetails = (placeId, retries = 2) => {
    return new Promise((resolve) => {
      if (!map) {
        resolve(null);
        return;
      }

      const service = new window.google.maps.places.PlacesService(map);

      const attemptGetDetails = (attemptsLeft) => {
        service.getDetails(
          {
            placeId: placeId,
            fields: [
              'opening_hours',
              'website',
              'url',
              'current_opening_hours',
              'secondary_opening_hours',
              'business_status',
              'permanently_closed'
            ]
          },
          (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(place);
            } else if (attemptsLeft > 0 && status === window.google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
              setTimeout(() => attemptGetDetails(attemptsLeft - 1), 1000);
            } else {
              resolve(null);
            }
          }
        );
      };

      attemptGetDetails(retries);
    });
  };

  // Replace your existing checkOpeningStatus function with this improved version:

  const checkOpeningStatus = (openingHours) => {
    if (!openingHours) return null;

    try {

      // Method 1: Check if isOpen method exists and is callable
      if (typeof openingHours.isOpen === 'function') {
        try {
          const isOpen = openingHours.isOpen();
          return isOpen;
        } catch (error) {
        }
      }

      // Method 2: Check if open_now property exists
      if (openingHours.hasOwnProperty('open_now')) {
        return openingHours.open_now;
      }

      // Method 3: Try to parse periods for current time
      if (openingHours.periods && openingHours.periods.length > 0) {

        const now = new Date();
        const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const currentTime = now.getHours() * 100 + now.getMinutes();

        // Find today's opening hours
        const todayPeriods = openingHours.periods.filter(period => {
          return period.open && period.open.day === currentDay;
        });

        if (todayPeriods.length === 0) {
          return false; // No opening hours for today
        }

        // Check if current time falls within any opening period
        for (const period of todayPeriods) {
          if (period.open && period.close) {
            const openTime = period.open.time ? parseInt(period.open.time) : 0;
            const closeTime = period.close.time ? parseInt(period.close.time) : 2359;

            if (currentTime >= openTime && currentTime <= closeTime) {
              return true;
            }
          } else if (period.open && !period.close) {
            // Open 24 hours
            return true;
          }
        }
        return false;
      }

      // Method 4: Check for alternative properties
      if (openingHours.isOpen !== undefined) {
        return openingHours.isOpen;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  // Helper function to get opening hours text
  const getOpeningHoursText = (openingHours) => {
    if (!openingHours) return null;

    try {
      if (openingHours.weekday_text && openingHours.weekday_text.length > 0) {
        return openingHours.weekday_text;
      }

      if (openingHours.periods && openingHours.periods.length > 0) {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const schedule = [];

        for (let day = 0; day < 7; day++) {
          const dayPeriods = openingHours.periods.filter(period =>
            period.open && period.open.day === day
          );

          if (dayPeriods.length === 0) {
            schedule.push(`${days[day]}: Closed`);
          } else {
            const times = dayPeriods.map(period => {
              if (period.open && period.close) {
                const openTime = period.open.time || '0000';
                const closeTime = period.close.time || '2359';
                return `${formatTime(openTime)} - ${formatTime(closeTime)}`;
              } else if (period.open && !period.close) {
                return 'Open 24 hours';
              }
              return 'Hours not available';
            }).join(', ');

            schedule.push(`${days[day]}: ${times}`);
          }
        }

        return schedule;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString || timeString.length !== 4) return timeString;

    const hours = parseInt(timeString.substring(0, 2));
    const minutes = timeString.substring(2, 4);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    return `${displayHours}:${minutes} ${period}`;
  };

  // Replace your existing processClinics function with this enhanced version:

  const processClinics = async (mapInstance, results) => {
    const newMarkers = [];
    const processedClinics = [];

    for (const place of results) {
      const marker = new window.google.maps.Marker({
        map: mapInstance,
        position: place.geometry.location,
        title: place.name,
      });

      const detailedPlace = await getPlaceDetails(place.place_id);

      // Enhanced opening hours detection with multiple fallbacks
      let isOpen = null;
      let openingHours = null;
      let openingHoursText = null;


      // Try to get opening hours from detailed place first
      if (detailedPlace) {
        // Check current_opening_hours first (most accurate)
        if (detailedPlace.current_opening_hours) {
          openingHours = detailedPlace.current_opening_hours;
          isOpen = checkOpeningStatus(openingHours);
          openingHoursText = getOpeningHoursText(openingHours);
        }

        // Fallback to regular opening_hours
        if (isOpen === null && detailedPlace.opening_hours) {
          openingHours = detailedPlace.opening_hours;
          isOpen = checkOpeningStatus(openingHours);
          openingHoursText = getOpeningHoursText(openingHours);
        }
      }

      // Fallback: Try to get opening hours from initial place data
      if (isOpen === null && place.opening_hours) {
        openingHours = place.opening_hours;
        isOpen = checkOpeningStatus(openingHours);
        openingHoursText = getOpeningHoursText(openingHours);
      }

      // Additional fallback: check for plus_code or other indicators
      if (isOpen === null && place.plus_code) {
        // For places with plus_code, we can assume they might be operational
        // but we'll leave it as unknown rather than guessing
        isOpen = null;
      }

      const placeWithDetails = {
        ...place,
        website: detailedPlace?.website || null,
        googleMapsUrl: detailedPlace?.url || null,
        isOpen: isOpen,
        openingHours: openingHours,
        openingHoursText: openingHoursText,
        address: place.vicinity || place.formatted_address
      };

      processedClinics.push(placeWithDetails);

      // Updated info window content with better opening hours display
      const openStatusHtml = isOpen !== null ?
        `<p style="color: ${isOpen ? 'green' : 'red'}; font-weight: bold; margin-bottom: 4px;">
${isOpen ? '🟢 Open now' : '🔴 Closed'}
</p>` :
        '<p style="color: #666; margin-bottom: 4px;">📍 Hours not available</p>';

      const infoWindow = new window.google.maps.InfoWindow({
        content: `
<div style="max-width: 250px;">
<h3 style="font-weight: bold; margin-bottom: 8px; color: #007177;">${place.name}</h3>
<p style="margin-bottom: 8px; color: #666;">${place.vicinity || place.formatted_address}</p>
${place.rating ? `<p style="margin-bottom: 8px;">⭐ ${place.rating}/5 (${place.user_ratings_total} reviews)</p>` : ''}
${openStatusHtml}
${detailedPlace?.website ?
            `<p style="margin-top: 8px;"><a href="${detailedPlace.website}" target="_blank" style="color: #007177; text-decoration: underline;">🌐 Visit Website</a></p>` :
            ''}
<p style="margin-top: 8px;"><a href="https://www.google.com/maps/place/?q=place_id:${place.place_id}" target="_blank" style="color: #007177; text-decoration: underline;">📍 View on Google Maps</a></p>
</div>
`
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });

      newMarkers.push(marker);
    }

    setMarkers(newMarkers);
    setClinics(processedClinics);
  };

  // Improved medical facility filtering function
  const isMedicalFacility = (place) => {
    const medicalTypes = [
      'hospital', 'doctor', 'health', 'clinic', 'pharmacy', 'dentist',
      'physiotherapist', 'medical_center', 'dental_clinic'
    ];

    const medicalKeywords = [
      'clinic', 'hospital', 'klinik', 'pusat kesihatan', 'medical centre',
      'medical center', 'doctor', 'doktor', 'specialist', 'pakar',
      'kesihatan', 'pergigian', 'dental', 'pharmacy', 'farmasi',
      'dispensary', 'poliklinik', 'polyclinic', 'health center',
      'healthcare', 'medical', 'surgery', 'dispensari'
    ];

    // Check place types
    const hasValidType = place.types && place.types.some(type =>
      medicalTypes.some(medType => type.toLowerCase().includes(medType))
    );

    // Check place name
    const hasValidName = medicalKeywords.some(keyword =>
      place.name.toLowerCase().includes(keyword.toLowerCase())
    );

    // Additional check for business status and operational places
    const isOperational = !place.business_status || place.business_status === 'OPERATIONAL';

    return (hasValidType || hasValidName) && isOperational;
  };

  const malaysianStates = [
    'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan',
    'Pahang', 'Perak', 'Perlis', 'Pulau Pinang', 'Sabah',
    'Sarawak', 'Selangor', 'Terengganu', 'Kuala Lumpur',
    'Labuan', 'Putrajaya'
  ];

  // State centers mapping
  const stateCenters = {
    'Johor': { lat: 1.4854, lng: 103.7618 },
    'Kedah': { lat: 6.1184, lng: 100.3685 },
    'Kelantan': { lat: 6.1254, lng: 102.2381 },
    'Melaka': { lat: 2.1896, lng: 102.2501 },
    'Negeri Sembilan': { lat: 2.7258, lng: 101.9424 },
    'Pahang': { lat: 3.8126, lng: 103.3256 },
    'Perak': { lat: 4.5921, lng: 101.0901 },
    'Perlis': { lat: 6.4414, lng: 100.1986 },
    'Pulau Pinang': { lat: 5.4164, lng: 100.3327 },
    'Sabah': { lat: 5.4204, lng: 116.7968 },
    'Sarawak': { lat: 1.5535, lng: 110.3593 },
    'Selangor': { lat: 3.0738, lng: 101.5183 },
    'Terengganu': { lat: 5.3117, lng: 103.1324 },
    'Kuala Lumpur': { lat: 3.139, lng: 101.6869 },
    'Labuan': { lat: 5.2831, lng: 115.2308 },
    'Putrajaya': { lat: 2.9264, lng: 101.6964 }
  };

  // Replace the entire useEffect hook with this updated version:

  useEffect(() => {
    const initMap = async () => {
      if (!googleMapsLoaded) return;

      setIsLoading(true);
      try {
        const mapElement = document.getElementById('map');
        if (!mapElement) throw new Error('Map container not found');

        if (!window.google?.maps?.Map || !window.google.maps.places) {
          throw new Error('Google Maps libraries not ready');
        }

        const mapInstance = new window.google.maps.Map(mapElement, {
          center: { lat: 3.139, lng: 101.6869 }, // Changed from Sabah coordinates to Kuala Lumpur coordinates
          zoom: 10,
          gestureHandling: 'cooperative',
          streetViewControl: false
        });

        setMap(mapInstance);
        setMapError(null);

        const service = new window.google.maps.places.PlacesService(mapInstance);

        // Sabah center and radius
        // const sabahCenter = { lat: 5.9804, lng: 116.0754 };

        // Kuala Lumpur center and radius
        const klCenter = { lat: 3.139, lng: 101.6869 }; // Changed from sabahCenter

        // More targeted search queries for medical facilities
        const searchQueries = [
          'clinic in Kuala Lumpur',           // Changed from 'clinic in Kota Kinabalu Sabah'
          'hospital in Kuala Lumpur',         // Changed from 'hospital in Kota Kinabalu Sabah'
          'klinik in Kuala Lumpur',           // Changed from 'klinik in Kota Kinabalu Sabah'
          'medical centre in Kuala Lumpur',   // Changed from 'medical centre in Kota Kinabalu Sabah'
          'poliklinik in Kuala Lumpur'        // Changed from 'poliklinik in Kota Kinabalu Sabah'
        ];

        let allResults = [];

        // Perform text search for each query
        for (const query of searchQueries) {
          const results = await new Promise((resolve) => {
            service.textSearch({
              query: query,
              location: klCenter,  // Changed from sabahCenter
              radius: 100000
            }, (results, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                resolve(results || []);
              } else {
                resolve([]);
              }
            });
          });

          allResults = [...allResults, ...results];
        }

        // Also do nearby search with medical types
        const medicalTypesInitial = ['hospital', 'doctor', 'health'];
        for (const mType of medicalTypesInitial) {
          const nearbyResults = await new Promise((resolve) => {
            service.nearbySearch({
              location: klCenter,  // Changed from sabahCenter
              radius: 100000,
              type: mType
            }, (results, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                resolve(results || []);
              } else {
                resolve([]);
              }
            });
          });
          allResults = [...allResults, ...nearbyResults];
        }

        // Remove duplicates
        const uniqueResults = Array.from(
          new Map(allResults.map(place => [place.place_id, place])).values()
        );

        // Filter for medical facilities only
        const medicalFacilities = uniqueResults
          .filter(place => isMedicalFacility(place))
          .sort((a, b) => {
            if (b.rating !== a.rating) {
              return (b.rating || 0) - (a.rating || 0);
            }
            return (b.user_ratings_total || 0) - (a.user_ratings_total || 0);
          });

        // This is the key change - make sure to await the processing
        await processClinics(mapInstance, medicalFacilities);

      } catch (error) {
        setMapError(`Failed to load initial clinics: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (googleMapsLoaded) {
      initMap();
    }
  }, [googleMapsLoaded]);

  // Improved search logic focused on medical facilities
  const searchClinics = async () => {
    if (!map || !googleMapsLoaded) {
      setMapError('Google Maps is not fully loaded yet. Please try again.');
      return;
    }

    setIsLoading(true);
    setMapError(null);
    setCurrentPage(1);

    try {
      // Clear existing markers
      markers.forEach(marker => marker.setMap(null));
      setMarkers([]);
      setClinics([]);

      const service = new window.google.maps.places.PlacesService(map);

      let searchLocation = stateCenters[searchParams.state];
      let searchRadius = 50000;

      map.setCenter(searchLocation);
      map.setZoom(9);

      let allResults = [];
      const isSpecificClinicSearch = searchParams.clinicName.trim().length > 0;

      if (isSpecificClinicSearch) {

        const specificSearchQueries = [
          `${searchParams.clinicName.trim()} clinic ${searchParams.state} Malaysia`,
          `${searchParams.clinicName.trim()} hospital ${searchParams.state} Malaysia`,
          `${searchParams.clinicName.trim()} klinik ${searchParams.state} Malaysia`,
          `${searchParams.clinicName.trim()} ${searchParams.state} Malaysia`,
        ];

        for (const query of specificSearchQueries) {
          // Text search
          const textResults = await new Promise((resolve) => {
            service.textSearch({
              query: query,
              location: searchLocation,
              radius: searchRadius
            }, (results, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                resolve(results || []);
              } else {
                resolve([]);
              }
            });
          });

          allResults = [...allResults, ...textResults];
        }

      } else {

        const medicalSearchQueries = [
          `clinic in ${searchParams.state} Malaysia`,
          `hospital in ${searchParams.state} Malaysia`,
          `klinik in ${searchParams.state} Malaysia`,
          `medical centre in ${searchParams.state} Malaysia`,
          `poliklinik in ${searchParams.state} Malaysia`,
          `pusat kesihatan in ${searchParams.state} Malaysia`,
          `health center in ${searchParams.state} Malaysia`
        ];

        // Text search for each medical query
        for (const query of medicalSearchQueries) {
          const textResults = await new Promise((resolve) => {
            service.textSearch({
              query: query,
              location: searchLocation,
              radius: searchRadius
            }, (results, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                resolve(results || []);
              } else {
                resolve([]);
              }
            });
          });

          allResults = [...allResults, ...textResults];
        }

        // Nearby search with medical types
        const medicalTypes = ['hospital', 'doctor', 'health'];
        for (const type of medicalTypes) {
          const nearbyResults = await new Promise((resolve) => {
            service.nearbySearch({
              location: searchLocation,
              radius: searchRadius,
              type: type
            }, (results, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                resolve(results || []);
              } else {
                resolve([]);
              }
            });
          });

          allResults = [...allResults, ...nearbyResults];
        }

        // Additional searches for major cities in larger states
        if (['Sabah', 'Sarawak'].includes(searchParams.state)) {
          const majorCities = {
            'Sabah': ['Kota Kinabalu', 'Sandakan', 'Tawau', 'Lahad Datu', 'Keningau'],
            'Sarawak': ['Kuching', 'Miri', 'Sibu', 'Bintulu']
          };

          const cities = majorCities[searchParams.state] || [];
          for (const city of cities) {
            const cityQueries = [
              `clinic in ${city} ${searchParams.state}`,
              `hospital in ${city} ${searchParams.state}`,
              `klinik in ${city} ${searchParams.state}`
            ];

            for (const query of cityQueries) {
              const cityResults = await new Promise((resolve) => {
                service.textSearch({
                  query: query,
                 }, (results, status) => {
                  if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    resolve(results || []);
                  } else {
                    resolve([]);
                  }
                });
              });

              allResults = [...allResults, ...cityResults];
            }
          }
        }
      }

      // Remove duplicates
      const uniqueResults = Array.from(
        new Map(allResults.map(place => [place.place_id, place])).values()
      );

      // STRICT FILTERING - Only medical facilities
      const medicalFacilities = uniqueResults.filter(place => {
        // First check if it's a medical facility
        if (!isMedicalFacility(place)) {
          return false;
        }

        // For specific clinic searches, check name matching
        if (isSpecificClinicSearch) {
          const clinicName = searchParams.clinicName.toLowerCase();
          const placeName = place.name.toLowerCase();

          // Check if the place name contains the searched clinic name
          if (placeName.includes(clinicName)) {
            return true;
          }

          // Check if it's a partial match
          const searchWords = clinicName.split(' ').filter(word => word.length > 2);
          const nameWords = placeName.split(' ');

          const matchingWords = searchWords.filter(searchWord =>
            nameWords.some(nameWord => nameWord.includes(searchWord))
          );

          // If most of the search words match, include it
          return matchingWords.length >= Math.ceil(searchWords.length * 0.6);
        }

        return true;
      });

      // Sort results
      const sortedFacilities = medicalFacilities.sort((a, b) => {
        if (isSpecificClinicSearch) {
          // For specific searches, prioritize exact matches
          const searchName = searchParams.clinicName.toLowerCase();
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          const aExactMatch = aName === searchName;
          const bExactMatch = bName === searchName;

          if (aExactMatch && !bExactMatch) return -1;
          if (!aExactMatch && bExactMatch) return 1;

          // Then by name similarity
          const aIncludes = aName.includes(searchName);
          const bIncludes = bName.includes(searchName);

          if (aIncludes && !bIncludes) return -1;
          if (!aIncludes && bIncludes) return 1;
        }

        // Default sorting by rating
        if (b.rating !== a.rating) {
          return (b.rating || 0) - (a.rating || 0);
        }
        return (b.user_ratings_total || 0) - (a.user_ratings_total || 0);
      });

      await processClinics(map, sortedFacilities);

    } catch (error) {
      setMapError(`Failed to search clinics: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const indexOfLastClinic = currentPage * clinicsPerPage;
  const indexOfFirstClinic = indexOfLastClinic - clinicsPerPage;
  const currentClinics = clinics.slice(indexOfFirstClinic, indexOfLastClinic);
  const totalPages = Math.ceil(clinics.length / clinicsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loadError) {
    return (
      <div className="text-red-500 p-4">
        <h2>Google Maps Loading Error</h2>
        <p>Error: {loadError.message}</p>
        <p>Please check:</p>
        <ul>
          <li>Your API key is correct</li>
          <li>Billing and API services are enabled</li>
          <li>Network connection is stable</li>
        </ul>
      </div>
    );
  }

  if (!googleMapsLoaded) {
    return (
      <div className="p-4 text-center">
        <p>Loading Google Maps... Please wait</p>
        <div className="animate-spin">🔄</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1">
          <div className="mb-4">
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              {/* <h3 className="text-lg font-medium text-gray-900 mb-4">Search Filters</h3> */}

              <div className="grid gap-4">
                <div>
                  <label htmlFor="state-select" className="block text-sm font-medium text-[#005a5f] mb-1">
                    Select State
                  </label>
                  <select
                    id="state-select"
                    className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500"
                    value={searchParams.state}
                    onChange={(e) => setSearchParams({ ...searchParams, state: e.target.value })}
                    disabled={isLoading}
                  >
                    {malaysianStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="clinic-search" className="block text-sm font-medium text-[#005a5f] mb-1">
                    Clinic Name
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-grow">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search
                          className="h-4 w-4 text-gray-400"
                          aria-hidden="true"
                          focusable="false"
                        />
                      </div>
                      <input
                        id="clinic-search"
                        type="text"
                        placeholder="Search by clinic name"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500"
                        value={searchParams.clinicName}
                        onChange={(e) => setSearchParams({ ...searchParams, clinicName: e.target.value })}
                        disabled={isLoading}
                      />
                    </div>
                    <Button
                      className="bg-[#FAF3AD] hover:bg-[#FBF6AD] text-[#005a5f] font-bold px-4 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007177] flex items-center"
                      onClick={searchClinics}
                      disabled={isLoading || !googleMapsLoaded}
                    >
                      {isLoading ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Searching...
                        </span>
                      ) : (
                        <>
                          <Search
                            className="w-4 h-4 mr-2"
                            aria-hidden="true"
                            focusable="false"
                          />
                          Search
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="h-[600px] overflow-y-auto">
            {mapError && (
              <div className="bg-teal-50 border-l-4 border-[#007177] p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-[#007177]"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-[#007177]">{mapError}</p>
                  </div>
                </div>
              </div>
            )}

            {isLoading && (
              <div className="flex flex-col items-center justify-center p-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007177] mb-4"></div>
                <p className="text-gray-600">Searching for clinics in {searchParams.state}...</p>
                <p className="text-gray-500 text-sm mt-2">This may take a moment for comprehensive results</p>
              </div>
            )}

            {!isLoading && clinics.length === 0 && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      No clinics found. Try searching for a specific clinic name
                      or selecting a different state.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!isLoading && clinics.length > 0 && (
              <div className="bg-teal-50 border-l-4 border-[#007177] p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-[#007177]"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-[#007177]">
                      Found{' '}
                      <span className="font-semibold">{clinics.length}</span>{' '}
                      clinics in{' '}
                      <span className="font-semibold">
                        {searchParams.state}
                      </span>
                      {searchParams.clinicName
                        ? ` matching "${searchParams.clinicName}"`
                        : ''}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {currentClinics.map((clinic, index) => (
              <Card key={`${clinic.place_id}-${index}`} className="mb-4 hover:shadow-lg transition-shadow duration-200 border-l-4 border-[#007177]">
                <CardContent className="p-5">
                  <div className="flex flex-col">
                    <h2 className="text-xl font-bold mb-3 text-gray-800 border-b pb-2">{clinic.name}</h2>

                    <div className="grid gap-2 mb-3">
                      <div className="flex items-start">
                        <MapPin
                          className="w-5 h-5 mr-2 text-gray-500 mt-0.5 flex-shrink-0"
                          aria-hidden="true"
                          focusable="false"
                        />
                        <p className="text-gray-700">{clinic.address}</p>
                      </div>

                      {clinic.rating && (
                        <div className="flex items-center">
                          <div className="flex mr-2">
                            {[...Array(5)].map((_, i) => (
                              <svg
                                key={i}
                                className={`w-4 h-4 ${i < Math.floor(clinic.rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                                fill="currentColor"
                                viewBox="0 0 20 20" aria-hidden="true"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <p className="text-gray-600 text-sm">
                            {clinic.rating.toFixed(1)} ({clinic.user_ratings_total} reviews)
                          </p>
                        </div>
                      )}

                      {/* {clinic.isOpen !== null && (
                        <div className="flex items-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${clinic.isOpen
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            <span className={`w-2 h-2 mr-1 rounded-full ${clinic.isOpen ? 'bg-green-600' : 'bg-red-600'
                              }`}></span>
                            {clinic.isOpen ? 'Open now' : 'Closed'}

                          </span>
                        </div>
                      )} */}

                    </div>

                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                      {clinic.website && (
                        <a
                          href={clinic.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1.5 bg-teal-50 text-[#007177] rounded-md text-sm hover:bg-teal-100 transition-colors"
                        >
                          <Globe
                            className="w-4 h-4 mr-1.5"
                            aria-hidden="true"
                            focusable="false"
                          />
                          Visit Website
                        </a>
                      )}
                      {clinic.googleMapsUrl && (
                        <a
                          href={clinic.googleMapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1.5 bg-teal-50 text-[#007177] rounded-md text-sm hover:bg-teal-100 transition-colors"
                        >
                          <svg
                            className="w-4 h-4 mr-1.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path
                              d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
                              fill="currentColor"
                            />
                          </svg>
                          View on Google Maps
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {clinics.length > 0 && (
              <div className="flex justify-center items-center mt-6 mb-2">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <Button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border ${currentPage === 1
                      ? 'border-gray-300 bg-white text-gray-300 cursor-not-allowed'
                      : 'border-gray-300 bg-white text-[#007177] hover:bg-teal-50'
                      }`}
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft
                      className="h-5 w-5"
                      aria-hidden="true"
                      focusable="false"
                    />
                  </Button>

                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>

                  <Button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border ${currentPage === totalPages
                      ? 'border-gray-300 bg-white text-gray-300 cursor-not-allowed'
                      : 'border-gray-300 bg-white text-[#007177] hover:bg-teal-50'
                      }`}
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight
                      className="h-5 w-5"
                      aria-hidden="true"
                      focusable="false"
                    />
                  </Button>
                </nav>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2 h-[600px] md:h-[800px] bg-gray-100 rounded-lg" id="map" />
      </div>
      <h2 className="text-xs mb-1 mt-4 text-gray-800 text-end">MY-LAM-00207 Jun/2025</h2>
    </div>
  );
};

export default MalaysiaClinicFinder;